import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { FaTelegramPlane } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/effect-coverflow";

export interface SkinPickerCarouselSkin {
  skinId: string;
  name: string;
  logo: string;
  previewImages: Array<string | { url: string; name?: string }>;
  previews?: Array<{ url: string; name: string }>;
  siteUrl: string;
  tglink?: string;
  weblink?: string;
  /** Optional; parent may attach API-driven defaults for theme seeding. */
  themeSeed?: {
    primaryColor: string;
    secondaryColor: string;
    headerColor: string;
    backgroundColor: string;
  } | null;
}

type SkinPickerCarouselProps = {
  skins: SkinPickerCarouselSkin[];
  selectedSkinId: string;
  onSelectSkin: (skinId: string) => void;
};

/** Viewport height for the phone preview stack. */
const STAGE_H_PX = 248;
const SLIDE_W_PX = 120;
const CARD_H_PX = 220;

function normalizeSkinSiteUrl(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

/** Draft `skinId` and list `skinId` may differ by whitespace or string vs number coercion. */
function skinRowIdEquals(a: string, b: string): boolean {
  return String(a ?? "").trim() === String(b ?? "").trim();
}

export function SkinPickerCarousel({
  skins,
  selectedSkinId,
  onSelectSkin,
}: SkinPickerCarouselProps) {
  const stripRef = useRef<HTMLDivElement>(null);
  const previewSwiperRef = useRef<SwiperType | null>(null);
  const maximizedSwiperRef = useRef<SwiperType | null>(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const [isMaximized, setIsMaximized] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window !== "undefined" ? window.innerHeight : 900
  );
  const [maximizeBounds, setMaximizeBounds] = useState({ top: 232, bottom: 80 });
  const [canScrollStripLeft, setCanScrollStripLeft] = useState(false);
  const [canScrollStripRight, setCanScrollStripRight] = useState(false);

  const updateStripScrollState = () => {
    const strip = stripRef.current;
    if (!strip) return;
    const edgeEpsilon = 2;
    const maxLeft = Math.max(0, strip.scrollWidth - strip.clientWidth);
    const left = Math.min(maxLeft, Math.max(0, strip.scrollLeft));
    const hasOverflow = maxLeft > edgeEpsilon;

    setCanScrollStripLeft(hasOverflow && left > edgeEpsilon);
    setCanScrollStripRight(hasOverflow && left < maxLeft - edgeEpsilon);
  };

  const selectedIdx = useMemo(
    () => skins.findIndex((s) => skinRowIdEquals(s.skinId, selectedSkinId)),
    [skins, selectedSkinId]
  );

  const selected = selectedIdx >= 0 ? skins[selectedIdx] : undefined;
  const hasSelection = selectedIdx >= 0;

  const visitSiteHref = useMemo(() => {
    if (!selected?.weblink) return null;
    return normalizeSkinSiteUrl(selected.weblink);
  }, [selected?.weblink]);

  const visitTelegramHref = useMemo(() => {
    if (!selected?.tglink) return null;
    return normalizeSkinSiteUrl(selected.tglink);
  }, [selected?.tglink]);

  const previewItems = useMemo(() => {
    if (!selected) return [];
    const nextApiPreviews = Array.isArray(selected.previews)
      ? selected.previews
          .map((p) => ({
            url: String((p as any)?.url ?? (p as any)?.Url ?? "").trim(),
            name: String((p as any)?.name ?? (p as any)?.Name ?? "").trim(),
          }))
          .filter((p) => p.url.length > 0)
      : [];
    if (nextApiPreviews.length > 0) return nextApiPreviews;

    const imgs = selected.previewImages?.filter(Boolean) ?? [];
    if (imgs.length > 0) {
      const objectStyle = imgs.some((img) => img && typeof img === "object");
      if (objectStyle) {
        return imgs
          .map((img) => ({
            url: String((img as any)?.url ?? (img as any)?.Url ?? "").trim(),
            name: String((img as any)?.name ?? (img as any)?.Name ?? "").trim(),
          }))
          .filter((p) => p.url.length > 0);
      }
      return imgs.map((url) => ({ url: String(url), name: "" }));
    }
    return [];
  }, [selected]);

  const count = previewItems.length;
  useEffect(() => {
    if (count > 0) {
      setFocusIndex(Math.floor((count - 1) / 2));
      return;
    }
    setFocusIndex(0);
  }, [selected?.skinId, count]);

  useEffect(() => {
    if (count <= 0) {
      setFocusIndex(0);
      return;
    }
    setFocusIndex((i) => Math.min(i, count - 1));
  }, [count]);

  useEffect(() => {
    previewSwiperRef.current?.slideTo(focusIndex);
    maximizedSwiperRef.current?.slideTo(focusIndex);
  }, [focusIndex]);

  /** Keep the horizontal skin strip scrolled so the selected thumb stays visible. */
  useEffect(() => {
    const idx = skins.findIndex((s) => skinRowIdEquals(s.skinId, selectedSkinId));
    if (idx < 0) return;
    const strip = stripRef.current;
    if (!strip) return;
    const btn = strip.querySelector<HTMLButtonElement>(`button:nth-child(${idx + 1})`);
    btn?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
  }, [skins, selectedSkinId]);

  useEffect(() => {
    if (!isMaximized) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsMaximized(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isMaximized]);

  useEffect(() => {
    if (!isMaximized) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMaximized]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setViewportHeight(window.innerHeight);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;

    updateStripScrollState();
    strip.addEventListener("scroll", updateStripScrollState, { passive: true });
    window.addEventListener("resize", updateStripScrollState);
    const rafId = window.requestAnimationFrame(updateStripScrollState);
    return () => {
      strip.removeEventListener("scroll", updateStripScrollState);
      window.removeEventListener("resize", updateStripScrollState);
      window.cancelAnimationFrame(rafId);
    };
  }, [skins, selectedSkinId]);

  useEffect(() => {
    if (!isMaximized || typeof window === "undefined") return;
    const updateBounds = () => {
      const vh = window.innerHeight;
      const stepperEl = document.querySelector<HTMLElement>('[data-wl-stepper="true"]');
      const footerEl = document.querySelector<HTMLElement>('[data-app-footer="true"]');
      const stepperBottom = Math.ceil(stepperEl?.getBoundingClientRect().bottom ?? 226);
      const footerTop = Math.floor(footerEl?.getBoundingClientRect().top ?? vh - 80);
      const top = Math.max(0, Math.min(stepperBottom + 6, vh - 220));
      const bottom = Math.max(0, Math.min(vh - footerTop, vh - top - 120));
      setMaximizeBounds({ top, bottom });
    };
    updateBounds();
    window.addEventListener("resize", updateBounds);
    window.addEventListener("scroll", updateBounds, true);
    return () => {
      window.removeEventListener("resize", updateBounds);
      window.removeEventListener("scroll", updateBounds, true);
    };
  }, [isMaximized]);

  const scrollStrip = (dir: -1 | 1) => {
    const el = stripRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 220, behavior: "smooth" });
    window.requestAnimationFrame(updateStripScrollState);
    window.setTimeout(updateStripScrollState, 180);
  };

  const onThumbClick = (skinId: string) => {
    onSelectSkin(skinRowIdEquals(selectedSkinId, skinId) ? "" : skinId);
  };

  const moveFocusBy = (dir: -1 | 1) => {
    if (count <= 1) return;
    setFocusIndex((prev) => {
      const next = prev + dir;
      if (next < 0) return count - 1;
      if (next >= count) return 0;
      return next;
    });
  };

  const onPreviewActivate = (index: number, isMaxMode: boolean) => {
    if (index !== focusIndex) {
      setFocusIndex(index);
      return;
    }
    if (isMaxMode) setIsMaximized(false);
    else setIsMaximized(true);
  };

  if (!skins.length) return null;
  const maximizedAvailableH = Math.max(280, viewportHeight - maximizeBounds.top - maximizeBounds.bottom);
  const maximizedCardH = Math.max(280, Math.min(860, maximizedAvailableH));
  const maximizedSlideW = Math.round(maximizedCardH * 0.46);
  const maximizedStageH = maximizedCardH;

  const renderPreviewStage = (layout: {
    stageH: number;
    slideW: number;
    cardH: number;
    mode?: "default" | "maximized";
  }) => (
    <div
      className="relative mx-auto w-full"
      style={{
        perspective: "1100px",
        perspectiveOrigin: "50% 45%",
      }}
    >
      <div
        role="presentation"
        className="relative mx-auto h-full max-w-full select-none overflow-hidden outline-none ring-0 [&_img]:border-0 [&_img]:outline-none [&_img]:ring-0 [&_img]:shadow-none"
        style={{ height: layout.stageH }}
      >
        <Swiper
          modules={[EffectCoverflow]}
          effect="coverflow"
          centeredSlides
          slidesPerView="auto"
          initialSlide={focusIndex}
          speed={340}
          className="h-full w-full"
          coverflowEffect={{
            rotate: layout.mode === "maximized" ? 0 : 0,
            stretch: 0,
            depth: layout.mode === "maximized" ? 40 : 0,
            modifier: 1,
            slideShadows: false,
          }}
          onSwiper={(swiper: SwiperType) => {
            if (layout.mode === "maximized") maximizedSwiperRef.current = swiper;
            else previewSwiperRef.current = swiper;
            swiper.slideTo(focusIndex, 0);
          }}
          onSlideChange={(swiper: SwiperType) => {
            if (swiper.activeIndex !== focusIndex) setFocusIndex(swiper.activeIndex);
          }}
        >
          {previewItems.map((item, i) => {
            const isFocused = i === focusIndex;
            const isMaxMode = layout.mode === "maximized";
            return (
              <SwiperSlide
                key={`${selected?.skinId}_${i}`}
                className="!flex cursor-pointer items-center justify-center"
                style={{
                  width: layout.slideW,
                  height: layout.cardH,
                  opacity: isFocused ? 1 : 0.9,
                  transition: "opacity 220ms ease",
                }}
                onClick={() => onPreviewActivate(i, isMaxMode)}
              >
                <div
                  data-carousel-card
                  className={`relative flex h-full w-full cursor-pointer flex-col overflow-hidden border-0 shadow-none outline-none ring-0 ${
                    isMaxMode ? "rounded-none" : "rounded-2xl"
                  }`}
                  style={{
                    transform: !isFocused ? (isMaxMode ? "scale(0.8)" : "scale(0.72)") : "scale(1)",
                    transformOrigin: "center center",
                    transition: "transform 240ms ease, filter 220ms ease",
                  }}
                >
                  <img
                    src={item.url}
                    alt=""
                    className={`w-full border-0 outline-none ring-0 ${
                      isMaxMode ? "h-full object-contain" : "h-[calc(100%-16px)] object-contain object-bottom"
                    }`}
                    style={{
                      maxHeight: "100%",
                      maxWidth: "100%",
                      transition: "filter 220ms ease",
                      filter: isFocused ? "none" : "blur(1.8px) brightness(1.18) saturate(0.96)",
                      objectPosition: isMaxMode ? "50% 50%" : undefined,
                    }}
                    draggable={false}
                  />
                  {!isMaxMode ? (
                    <div className="pointer-events-none flex h-4 items-center justify-center px-2 pb-0">
                      {item.name ? (
                        <span className="text-[9px] font-semibold uppercase tracking-wider text-zinc-100">
                          {item.name}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>

        {count > 1 ? (
          <>
            <button
              type="button"
              className="absolute inset-y-0 left-0 z-[65] w-[34%] bg-transparent"
              aria-label="Focus previous preview"
              onClick={() => moveFocusBy(-1)}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 z-[65] w-[34%] bg-transparent"
              aria-label="Focus next preview"
              onClick={() => moveFocusBy(1)}
            />
            <button
              type="button"
              className="absolute left-2 top-1/2 z-[70] -translate-y-1/2 rounded-full bg-[var(--border-color)] p-1 text-white shadow-[0_6px_16px_rgba(0,0,0,0.35)] hover:opacity-95"
              aria-label="Previous preview"
              onClick={() => moveFocusBy(-1)}
            >
              <ChevronLeftRoundedIcon sx={{ fontSize: 16 }} />
            </button>
            <button
              type="button"
              className="absolute right-2 top-1/2 z-[70] -translate-y-1/2 rounded-full bg-[var(--border-color)] p-1 text-white shadow-[0_6px_16px_rgba(0,0,0,0.35)] hover:opacity-95"
              aria-label="Next preview"
              onClick={() => moveFocusBy(1)}
            >
              <ChevronRightRoundedIcon sx={{ fontSize: 16 }} />
            </button>
          </>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="mt-1">
      <div className="overflow-hidden rounded-xl border border-border-color/40 bg-[color-mix(in_srgb,var(--table-header,#0a0a0a)_78%,#0f172a)]">
        <div className="relative z-10 mx-auto w-full">
          <div className="overflow-hidden bg-transparent">
          <div
            role="region"
            aria-roledescription="carousel"
            aria-label={selected ? `${selected.name} previews` : "Skin preview"}
          >
            {hasSelection && count > 0 ? (
              <>
                {renderPreviewStage({
                  stageH: STAGE_H_PX,
                  slideW: SLIDE_W_PX,
                  cardH: CARD_H_PX,
                  mode: "default",
                })}

              <div className="-mt-5">
                {count > 1 ? (
                  <div
                    className="relative flex justify-center gap-1.5 border-t border-border-color/25 px-2 pt-0 pb-0.5"
                    role="tablist"
                    aria-label="Preview images"
                  >
                    {previewItems.map((_, i) => (
                      <button
                        key={`dot_${selected?.skinId}_${i}`}
                        type="button"
                        role="tab"
                        aria-selected={i === focusIndex}
                        className={`h-1.5 min-w-1.5 rounded-full transition-all duration-200 ${
                          i === focusIndex
                            ? "w-6 bg-[color:var(--border-color)]"
                            : "w-1.5 bg-zinc-600 hover:bg-zinc-500"
                        }`}
                        onClick={() => setFocusIndex(i)}
                        aria-label={`Preview ${i + 1} of ${count}`}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="h-1.5 border-t border-border-color/40" aria-hidden />
                )}
              </div>
              <div className="h-1 border-t border-border-color/25" aria-hidden />
            </>
          ) : hasSelection ? (
            <div
              className="flex flex-col items-center justify-center px-6 text-center text-sm text-zinc-500"
              style={{ minHeight: STAGE_H_PX }}
            >
              No previews available
            </div>
          ) : (
            <div
              className="flex flex-col items-center justify-center px-6 text-center text-sm text-zinc-500"
              style={{ minHeight: STAGE_H_PX }}
            >
              Choose a skin below
            </div>
          )}
          </div>
        </div>
      </div>
        <div className="border-t border-border-color/35 px-2 pt-1.5 pb-1.5">
        {hasSelection && (visitTelegramHref || visitSiteHref) ? (
          <div className="mb-2 border-b border-border-color/35 pb-2">
            <div className="flex items-center justify-between gap-2">
              <p className="inline-flex items-center gap-1 text-[12px] font-semibold uppercase tracking-[0.08em] text-[color:var(--border-color)]">
                <VisibilityRoundedIcon sx={{ fontSize: 14, color: "currentColor" }} />
                <span>View demo</span>
              </p>
              <div className="flex items-center gap-1.5">
              {visitTelegramHref ? (
                <a
                  href={visitTelegramHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-7 animate-pulse items-center justify-center gap-1 rounded-full border border-[color-mix(in_srgb,var(--border-color)_56%,var(--border-color))] bg-[color-mix(in_srgb,var(--border-color)_16%,transparent)] px-2.5 text-[10px] font-semibold text-[color:var(--border-color)] transition hover:bg-[color-mix(in_srgb,var(--border-color)_24%,transparent)]"
                  aria-label="Open Telegram demo"
                  title="Open Telegram demo"
                >
                  <FaTelegramPlane size={13} />
                  <span>Telegram</span>
                </a>
              ) : null}
              {visitSiteHref ? (
                <a
                  href={visitSiteHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-7 animate-pulse items-center justify-center gap-1 rounded-full border border-[color-mix(in_srgb,var(--border-color)_56%,var(--border-color))] bg-[color-mix(in_srgb,var(--border-color)_16%,transparent)] px-2.5 text-[10px] font-semibold text-[color:var(--border-color)] transition hover:bg-[color-mix(in_srgb,var(--border-color)_24%,transparent)]"
                  aria-label="Open Website demo"
                  title="Open Website demo"
                >
                  <LanguageRoundedIcon sx={{ fontSize: 14, color: "currentColor" }} />
                  <span>Website</span>
                </a>
              ) : null}
              </div>
            </div>
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-3 px-0.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
            Available skins
          </span>
          {hasSelection ? (
            <span className="text-[12px] font-medium text-[color:var(--border-color)]">
              {selected?.name}
            </span>
          ) : (
            <span className="text-[10px] text-zinc-600">Select to preview</span>
          )}
        </div>

        <div className="relative mt-1">
          <div
            ref={stripRef}
            className="flex min-h-16 flex-1 flex-nowrap gap-1.5 overflow-x-auto overflow-y-hidden pr-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {skins.map((skin) => {
              const active = skinRowIdEquals(selectedSkinId, skin.skinId);
              const logoSrc = skin.logo?.trim() ?? "";
              return (
                <button
                  key={skin.skinId}
                  type="button"
                  onClick={() => onThumbClick(skin.skinId)}
                  className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 outline-none transition-all duration-200 focus-visible:ring-2 focus-visible:ring-[color:var(--border-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--table-header,#0a0a0a)] ${
                    active
                      ? "z-10 border-[color:var(--border-color)] bg-[color-mix(in_srgb,var(--border-color)_10%,#0a1324)] opacity-100 shadow-[0_0_0_1px_color-mix(in_srgb,var(--border-color)_45%,transparent)]"
                      : "border-border-color/80 bg-black/20 opacity-80 saturate-90 hover:border-zinc-500 hover:opacity-95 hover:saturate-100"
                  }`}
                  aria-pressed={active}
                  aria-label={`Select skin ${skin.name}`}
                >
                  {logoSrc ? (
                    <img
                      src={logoSrc}
                      alt=""
                      className="h-full w-full border-0 object-contain bg-black/35 p-1.5 outline-none ring-0"
                    />
                  ) : (
                    <span className="flex h-full w-full items-center justify-center bg-zinc-800 text-[10px] text-zinc-500">
                      —
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {canScrollStripLeft ? (
            <button
              type="button"
              className="absolute left-1 top-1/2 z-10 -translate-y-1/2 rounded-full bg-[var(--border-color)] p-1 text-white shadow-[0_6px_16px_rgba(0,0,0,0.35)] hover:opacity-95"
              aria-label="Scroll skins left"
              onClick={() => scrollStrip(-1)}
            >
              <ChevronLeftRoundedIcon sx={{ fontSize: 16 }} />
            </button>
          ) : null}
          {canScrollStripRight ? (
            <button
              type="button"
              className="absolute right-1 top-1/2 z-10 -translate-y-1/2 rounded-full bg-[var(--border-color)] p-1 text-white shadow-[0_6px_16px_rgba(0,0,0,0.35)] hover:opacity-95"
              aria-label="Scroll skins right"
              onClick={() => scrollStrip(1)}
            >
              <ChevronRightRoundedIcon sx={{ fontSize: 16 }} />
            </button>
          ) : null}
        </div>
        </div>
      </div>
      {isMaximized && typeof document !== "undefined"
        ? createPortal(
            <div
              className="fixed inset-x-0 z-[2147483647] flex items-center justify-center overflow-hidden px-0"
              style={{ top: maximizeBounds.top, bottom: maximizeBounds.bottom }}
              onClick={() => setIsMaximized(false)}
              role="dialog"
              aria-modal="true"
              aria-label="Maximized skin preview carousel"
            >
              <div
                className="h-full w-screen overflow-hidden bg-[linear-gradient(180deg,color-mix(in_srgb,var(--border-color)_18%,#08121f)_0%,color-mix(in_srgb,var(--table-header,#060b15)_86%,#04070f)_36%,color-mix(in_srgb,var(--table-header,#060b15)_92%,#02040a)_100%)]"
                onClick={(e) => e.stopPropagation()}
              >
                {renderPreviewStage({
                  stageH: maximizedStageH,
                  slideW: maximizedSlideW,
                  cardH: maximizedCardH,
                  mode: "maximized",
                })}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
